import { Input } from 'antd';
import type { InputProps, InputRef } from 'antd';
import { forwardRef } from 'react';
import { CONTROL_CLASS } from '@/theme/controlSizing';

/**
 * Konteks pemakaian textbox.
 * - `page`: filter / inline editor di list page → density `small`.
 * - `form`: form input modal & drawer → density `middle`.
 * - `auth`: halaman autentikasi (login/registrasi) → density `middle` (sama
 *   dengan `form`, namun secara semantik membedakan konteks dari form bisnis).
 *
 * Default: `form`.
 */
export type InputContext = 'page' | 'form' | 'auth';

export interface TextInputProps extends Omit<InputProps, 'size'> {
  /** Konteks pemakaian (lihat {@link InputContext}). Default: `form`. */
  context?: InputContext;
  /** Override paksa size Antd jika perlu (jarang dipakai). */
  size?: InputProps['size'];
}

/**
 * BASE: Textbox untuk teks/string.
 *
 * - Rata kiri (default Antd Input).
 * - Density mengikuti `context` (page=small, form=middle).
 *
 * Pemakaian:
 * ```tsx
 * <TextInput placeholder="Cari..." context="page" />
 * <TextInput value={nama} onChange={...} />  // default form
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */
export const TextInput = forwardRef<InputRef, TextInputProps>(function TextInput(
  { context = 'form', size, className, ...rest },
  ref
) {
  const resolvedSize = size ?? (context === 'page' ? 'small' : 'middle');
  return (
    <Input
      ref={ref}
      size={resolvedSize}
      className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
      {...rest}
    />
  );
});
